import { Suspense } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import classNames from "classnames";
import { Icon } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import {
  AssetType,
  editingNodeAtom,
  fetchChatbotAsyncAtomFamily,
  fetchInterfaceProjectAsyncAtomFamily,
  fetchTableFieldsAsyncAtomFamily,
  isValidUUID,
  previewNodeIdAtom,
} from "@zapier/canvas-state";
import styles from "./EditAssetButton.module.css";

type Props = {
  id: string;
  assetType: AssetType;
  assetId: string;
  interfaceProjectId: string;
  hoveringOverEditButton: boolean;
};

function SuspendedComponent({
  id,
  assetType,
  assetId,
  interfaceProjectId,
  hoveringOverEditButton,
}: Props) {
  const tableId = assetType === "table" ? assetId : undefined;
  const interfaceId =
    assetType === "interface" ? interfaceProjectId : undefined;
  const chatbotId = assetType === "chatbot" ? assetId : undefined;

  const tableFields = useAtomValue(fetchTableFieldsAsyncAtomFamily(tableId));
  const interfaceProject = useAtomValue(
    fetchInterfaceProjectAsyncAtomFamily(interfaceId),
  );
  const chatbot = useAtomValue(fetchChatbotAsyncAtomFamily(chatbotId));
  const isDraftMode = !isValidUUID(id);

  const setEditingNode = useSetAtom(editingNodeAtom);
  const setPreviewNodeId = useSetAtom(previewNodeIdAtom);

  const openEditModal = () => {
    if (isDraftMode) {
      setEditingNode(id);
    } else {
      setPreviewNodeId(id);
    }
  };

  if (tableFields === null || interfaceProject === null || chatbot === null)
    return null;

  return (
    <span
      className={classNames(styles.editButton, {
        [styles.hovered]: hoveringOverEditButton,
      })}
    >
      <Button size="medium" variant="primary" onPress={openEditModal}>
        <Icon name="actionEditAi" />
        {hoveringOverEditButton ? "Edit asset" : null}
      </Button>
    </span>
  );
}

export function EditAssetButton({
  id,
  assetType,
  assetId,
  interfaceProjectId,
  hoveringOverEditButton,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent
        id={id}
        assetType={assetType}
        assetId={assetId}
        interfaceProjectId={interfaceProjectId}
        hoveringOverEditButton={hoveringOverEditButton}
      />
    </Suspense>
  );
}
